import { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../app/hook";
import {
  Button,
  Form,
  FormField,
  SpaceBetween,
  ColumnLayout,
  Box,
} from "@cloudscape-design/components-themed";
import { LABELS } from "./constants";
import {
  COMMON_LABELS,
  getDivisionItems,
  getDivisionDict,
  getFirstDivisionDict,
  DivisionDict,
  stringToBoolean,
} from "../default-constants";
import { showModal } from "../UI/modal/modal-slice";
import { FormCpInfoBlock, FormContractDetailsBlock } from "./Forms";
import { getMessage } from "../../features/error/messages";
import { isNull, isLengthValid } from "../../utils/inputValidation";
import { formatDate, formatDateHyphen } from "../../utils/format";
import {
  GatewayCompaniesContracts,
  Division,
  GatewayCompaniesContractsUpdateInfo,
  GatewayCompaniesInfo,
  GatewayCompaniesContractsUpdate,
} from "../../features/api-interfaces";
import {
  usePostGatewayCompaniesContractsMutation,
  useGetDivisionQuery,
  usePutGatewayCompaniesContractsUpdateMutation,
  useGetGatewayCompaniesInfosQuery,
} from "../../features/api";

//=================================================================
// GW契約内容登録の画面クラス
//概要：事業者情報更新画面のGW新規登録ボタンから遷移、GWの新規登録を行う
//
//前の画面からの必要なパラメーター
//事業者名カナ cpNameKana
//事業者名     cpName
//法人番号     copNum
//事業者コード cpCode(パラメータ)
//
//遷移先：このページに来る前の事業者情報更新画面
//戻り先：このページに来る前の事業者情報更新画面
//
//==================================================================

export const RegisterInfo = (props: any) => {
  //クエリパラメータとパスパラメータ
  const { gwCompanyCode, gatewayNo } = useParams();
  const search = useLocation().search;
  const query = new URLSearchParams(search);

  //戻るボタンの表示確認フラグ
  const [changeFlag, setChangeFlag] = useState(false);

  //各種変数の宣言
  const navigate = useNavigate();
  const [valueGwCord, setValueGwCord] = useState(""); //GWコード
  const [valueGwName, setValueGwName] = useState(""); //GW名
  const [valueGwStatus, setValueGwStatus] = useState(""); //契約ステータス
  const [valueGwContractedUsageRate, setValueGwContractedUsageRate] =
    useState(""); //契約利用料率
  const [valueStartDate, setValueStartDate] = useState(""); //申込開始日
  const [valueGwManagement, setValueGwManagement] = useState(true); //口座情報管理要否
  const [valueGwPayment, setValueGwPayment] = useState(true); //IPID事前払出要否

  const valueCpCode = gwCompanyCode as string; //事業者コード
  let valueCopNum = query.get("copNum"); //法人番号
  let valueCpName = query.get("cpName"); //事業者名
  let valueCpNameKana = query.get("cpNameKana"); //事業者カナ名

  const navigator = useNavigate();
  const [gatewayCompaniesContracts] =
    usePostGatewayCompaniesContractsMutation();
  const [putGatewayCompaniesContractsUpdate] =
    usePutGatewayCompaniesContractsUpdateMutation();

  // エラーメッセージ関連
  const [contractedUsageRateErrorMsg, setContractedUsageRateErrorMsg] =
    useState("");
  const [gwNameErrorMsg, setGwNameErrorMsg] = useState("");
  const [gwStatusErrorMsg, setGwStatusErrorMsg] = useState("");
  const [gwStartDateErrorMsg, setGwStartDateErrorMsg] = useState("");

  //要不要ラベルと操作可能の設定
  const necessaryItems = [
    { value: true, label: LABELS.NECESSARY, disabled: false },
    { value: false, label: LABELS.UNNECESSARY, disabled: false },
  ];

  //契約ステータス
  const contractItems = [
    { value: "false", label: LABELS.STATUS_AGREE, disabled: false },
    { value: "true", label: LABELS.STATUS_STOP, disabled: false },
  ];

  // 区分取得API呼び出し(親区分コード：3:バーコード)
  const divisionData = useGetDivisionQuery("3");
  const gatewayFormDivisions = divisionData?.data as Division[];
  const gatewayFormItems: DivisionDict[] =
    getDivisionItems(gatewayFormDivisions);
  const [gatewayFormKbn, setGatewayFormKbn] = useState<DivisionDict>({
    label: "",
    value: "",
  });

  const dispatch = useAppDispatch();

  // 以下より処理開始
  const registeredData: GatewayCompaniesContractsUpdate = props.registeredData;

  // 事業者情報の取得
  const { data: companiesData } = useGetGatewayCompaniesInfosQuery(valueCpCode);
  const gwCompanyData = companiesData as GatewayCompaniesInfo;
  if (gwCompanyData !== undefined) {
    valueCopNum = gwCompanyData.corporateNumber;
    valueCpName = gwCompanyData.gwCompanyName;
    valueCpNameKana = gwCompanyData.gwCompanyNameKana;
  }

  useEffect(() => {
    setGatewayFormKbn(getFirstDivisionDict(gatewayFormItems));
    if (registeredData !== undefined && gatewayNo !== undefined) {
      // 更新時
      const gatewayFormKbnRegistered = getDivisionDict(
        gatewayFormItems,
        String(registeredData.contractServiceCode)
      );
      setValueGwCord(gatewayNo);
      setValueGwName(registeredData.gwName);
      setGatewayFormKbn(gatewayFormKbnRegistered);
      setValueGwStatus(String(registeredData.contractStoppedFlag));
      setValueStartDate(formatDateHyphen(registeredData.applicationStartDate));
      setValueGwContractedUsageRate(
        registeredData.contractualUsageRateAgreementDetails
      );
      setValueGwManagement(registeredData.accountInfoManagementRequiredFlag);
      setValueGwPayment(registeredData.priorPayoutIpidRequiredFlag);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registeredData, divisionData]);

  //各項目のバリデーションチェック
  //契約利用料率合意内容_文字数チェック
  const contractedUsageRateCheck = (check: string) => {
    if (!isLengthValid(check, 2000)) {
      setContractedUsageRateErrorMsg(getMessage("KGRERR058", 2000).label);

      return false;
    }
    // ここまで来たら問題ないのでエラーを消す
    setContractedUsageRateErrorMsg("");
    return true;
  };

  //GW名項目チェック
  const gwNameOnChange = (gwName: string) => {
    if (isNull(gwName)) {
      setGwNameErrorMsg(getMessage("KGRERR055").label);
      return false;
    } else if (!isLengthValid(gwName, 100)) {
      setGwNameErrorMsg(getMessage("KGRERR058", 100).label);
      return false;
    }
    // ここまで来たら問題ないのでエラーを消す
    setGwNameErrorMsg("");
    return true;
  };

  //契約ステータスチェック
  const gwStatusOnChange = (gwStatus: string) => {
    if (isNull(gwStatus)) {
      setGwStatusErrorMsg(getMessage("KGRERR055").label);
      return false;
    }
    // ここまで来たら問題ないのでエラーを消す
    setGwStatusErrorMsg("");
    return true;
  };

  //申し込み開始日時項目チェック
  const gwStartDateOnChange = (gwStartDate: string) => {
    if (isNull(gwStartDate)) {
      setGwStartDateErrorMsg(getMessage("KGRERR055").label);

      return false;
    }
    // ここまで来たら問題ないのでエラーを消す
    setGwStartDateErrorMsg("");
    return true;
  };

  /**
   * OKボタンを押下イベント関数
   */
  const onClickOKButton = () => {
    if (validateInputs()) {
      if (registeredData !== undefined) {
        // 更新時
        const gatewayCompaniesContractsUpdate: GatewayCompaniesContractsUpdate =
          {
            gwCompanyCode: valueCpCode,
            gwName: valueGwName,
            contractServiceCode: gatewayFormKbn.value,
            contractStoppedFlag: stringToBoolean(valueGwStatus),
            contractualUsageRateAgreementDetails: valueGwContractedUsageRate,
            applicationStartDate: formatDate(valueStartDate),
            accountInfoManagementRequiredFlag: valueGwManagement,
            priorPayoutIpidRequiredFlag: valueGwPayment,
          };
        const data: GatewayCompaniesContractsUpdateInfo = {
          gatewayNo: Number(valueGwCord),
          data: gatewayCompaniesContractsUpdate,
        };
        dispatch(
          showModal({
            title: COMMON_LABELS.TITLES.DL,
            content: COMMON_LABELS.CONTENTS.UPDATE_CONFIRM,
            negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
            positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
            positiveFn: () => {
              putGatewayCompaniesContractsUpdate(data).then((result: any) => {
                if (!("error" in result)) {
                  props.refetch();
                  navigator(`/gateway-companies/${gwCompanyCode}`);
                }
              });
            },
          })
        );
      } else {
        // 初期登録時
        const data: GatewayCompaniesContracts = {
          gwCompanyCode: valueCpCode,
          gwName: valueGwName,
          contractServiceCode: gatewayFormKbn.value,
          contractStoppedFlag: stringToBoolean(valueGwStatus),
          contractualUsageRateAgreementDetails: valueGwContractedUsageRate,
          applicationStartDate: formatDate(valueStartDate),
          accountInfoManagementRequiredFlag: valueGwManagement,
          priorPayoutIpidRequiredFlag: valueGwPayment,
        };
        dispatch(
          showModal({
            title: COMMON_LABELS.TITLES.DL,
            content: COMMON_LABELS.CONTENTS.REGISTER_CONFIRM,
            negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
            positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
            positiveFn: () => {
              gatewayCompaniesContracts(data).then((result) => {
                if (!("error" in result)) {
                  navigator(`/gateway-companies/${valueCpCode}`);
                }
              });
            },
          })
        );
      }
    }
  };

  //登録ボタンクリック時のバリデーションチェック
  const validateInputs = () => {
    let flag = true;
    const checks = [
      gwNameOnChange(valueGwName),
      gwStartDateOnChange(valueStartDate),
      contractedUsageRateCheck(valueGwContractedUsageRate),
      gwStatusOnChange(valueGwStatus),
    ];
    checks.forEach(function (value) {
      if (value === false) {
        flag = false;
      }
    });
    return flag;
  };

  //戻るボタン押下時のダイアログ
  const returnToList = () => {
    if (!changeFlag) {
      navigate(`/gateway-companies/${valueCpCode}`);
    } else {
      dispatch(
        showModal({
          title: COMMON_LABELS.TITLES.DL,
          content: COMMON_LABELS.CONTENTS.PAGE_MOVE_CONFIRM,
          positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
          negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
          positiveFn: () => {
            navigate(`/gateway-companies/${valueCpCode}`);
          },
        })
      );
    }
  };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <FormField stretch>
        <Form>
          <SpaceBetween size="l">
            <FormCpInfoBlock
              cpCode={valueCpCode}
              copNum={valueCopNum}
              cpNameKana={valueCpNameKana}
              cpName={valueCpName}
            />
            {/*=== 契約内容の画面はここから ===*/}
            <FormField stretch>
              <SpaceBetween size="l">
                <FormContractDetailsBlock
                  setChangeFlag={setChangeFlag}
                  setGwCord={setValueGwCord}
                  gwCord={valueGwCord}
                  setGwName={setValueGwName}
                  gwName={valueGwName}
                  selectedGwService={gatewayFormKbn}
                  setSelectedGwService={setGatewayFormKbn}
                  ServiceItems={gatewayFormItems}
                  setValueGwStatus={setValueGwStatus}
                  valueGwStatus={valueGwStatus}
                  setValueStartDate={setValueStartDate}
                  gwStatusErrorMsg={gwStatusErrorMsg}
                  gwStatusOnChange={gwStatusOnChange}
                  valueStartDate={valueStartDate}
                  setValueGwContractedUsageRate={setValueGwContractedUsageRate}
                  setValueGwManagement={setValueGwManagement}
                  valueGwManagement={valueGwManagement}
                  setValueGwPayment={setValueGwPayment}
                  valueGwPayment={valueGwPayment}
                  contractItems={contractItems}
                  necessaryItems={necessaryItems}
                  valueGwContractedUsageRate={valueGwContractedUsageRate}
                  contractedUsageRateCheck={contractedUsageRateCheck}
                  contractedUsageRateErrorMsg={contractedUsageRateErrorMsg}
                  gwNameOnChange={gwNameOnChange}
                  gwStartDateOnChange={gwStartDateOnChange}
                  gwNameErrorMsg={gwNameErrorMsg}
                  gwStartDateErrorMsg={gwStartDateErrorMsg}
                />
              </SpaceBetween>
            </FormField>
            <ColumnLayout columns={2}>
              <Box float="left">
                <Button iconAlign="left" variant="link" onClick={returnToList}>
                  {COMMON_LABELS.BTNS.BACK}
                </Button>
              </Box>
              {registeredData ? (
                <Box float="right">
                  <Button
                    iconAlign="right"
                    onClick={onClickOKButton}
                    variant="primary"
                  >
                    {COMMON_LABELS.BTNS.UPDATE}
                  </Button>
                </Box>
              ) : (
                <Box float="right">
                  <Button
                    iconAlign="right"
                    onClick={onClickOKButton}
                    variant="primary"
                  >
                    {COMMON_LABELS.BTNS.REGISTER}
                  </Button>
                </Box>
              )}
            </ColumnLayout>
          </SpaceBetween>
        </Form>
      </FormField>
    </form>
  );
};
